import type { JsonValue } from './types'

// The two declarable primitives JSON has no room for. Boxed rather than stringified, so decoding can tell a
// `BigInt('12')` from the string `'12'` — which matters, because `z.bigint()` rejects the string.

const isBox = (value: unknown, key: '$bigint' | '$date'): boolean =>
  !!value && typeof value === 'object' && key in (value as Record<string, unknown>)

/** A declared value (a `.default()`, an enum member, a constraint bound) on its way out. */
export function toJsonValue(value: unknown): JsonValue {
  if (typeof value === 'bigint') {
    return { $bigint: value.toString() }
  }

  if (value instanceof Date) {
    return { $date: value.toISOString() }
  }

  if (Array.isArray(value)) {
    return value.map(toJsonValue)
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, inner]) => [key, toJsonValue(inner)]))
  }

  return value as JsonValue
}

/** The same value on its way back in. */
export function fromJsonValue(value: JsonValue): unknown {
  if (isBox(value, '$bigint')) {
    return BigInt((value as { $bigint: string }).$bigint)
  }

  if (isBox(value, '$date')) {
    return new Date((value as { $date: string }).$date)
  }

  if (Array.isArray(value)) {
    return value.map(fromJsonValue)
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, inner]) => [key, fromJsonValue(inner)]))
  }

  return value
}
